/**
 * Admin guide (E13-F02), registered as `renderView('help_admin')`.
 *
 * Reachable from the "Admin guide" card on `/help` (admin-only, see
 * `HelpView.tsx`) or by direct URL — same shallow access pattern as every
 * other admin-only screen in this app (`/users` is not hard-guarded either;
 * see decision-log.md, Help E13-F02). Same TOC + article grid as `HelpView.tsx`,
 * just scoped to the `help_admin` page of the shared catalogue.
 */
import { useState } from 'react';
import { filterHelpTopicsForPage } from './help-sections.js';
import { HelpToc } from './HelpToc.js';
import { AdminWelcome } from './sections/AdminWelcome.js';
import { AdminUsers } from './sections/AdminUsers.js';
import { AdminSourcingTeams } from './sections/AdminSourcingTeams.js';
import { AdminTeams } from './sections/AdminTeams.js';
import { AdminModelMatrix } from './sections/AdminModelMatrix.js';
import { AdminAddressBook } from './sections/AdminAddressBook.js';
import { AdminPositionTypes } from './sections/AdminPositionTypes.js';
import { AdminQuestionnaire } from './sections/AdminQuestionnaire.js';
import { AdminResearchSettings } from './sections/AdminResearchSettings.js';

export interface HelpAdminViewProps {
  /** Return to the main `/help` guide (the sidebar's Help button also works). */
  readonly onBack: () => void;
}

export function HelpAdminView({ onBack }: HelpAdminViewProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const topics = filterHelpTopicsForPage('help_admin', query);

  return (
    <div
      data-testid="help-admin-view"
      className="grid items-start gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]"
    >
      <HelpToc page="help_admin" topics={topics} query={query} onQueryChange={setQuery} />

      <article className="min-w-0 space-y-6">
        <button
          type="button"
          data-testid="help-admin-back"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-text-strong"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden />
          Back to Help
        </button>
        <AdminWelcome />
        <AdminUsers />
        <AdminSourcingTeams />
        <AdminTeams />
        <AdminModelMatrix />
        <AdminAddressBook />
        <AdminPositionTypes />
        <AdminQuestionnaire />
        <AdminResearchSettings />
      </article>
    </div>
  );
}
